// Histórico do ticket no padrão Aeros: linha do tempo agrupada por dia,
// ponto colorido por tipo de evento, ator/fila, hora e tempo decorrido
// desde a etapa anterior. Acessos repetidos são colapsados e podem ser
// ocultados. Regras puras em ./ticketLogRules.js.
import React, { useState, useEffect, useMemo } from "react";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Switch from "@material-ui/core/Switch";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloseIcon from "@material-ui/icons/Close";
import HistoryIcon from "@material-ui/icons/History";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import VisibilityIcon from "@material-ui/icons/Visibility";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import CallReceivedIcon from "@material-ui/icons/CallReceived";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ReplayIcon from "@material-ui/icons/Replay";
import AndroidIcon from "@material-ui/icons/Android";
import LowPriorityIcon from "@material-ui/icons/LowPriority";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import useAerosSettingsStyles from "../Settings/aerosSettingsStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { buildTimeline, describeLog, toneOf, timeLabel, countByType } from "./ticketLogRules";

const ICONS = {
  create: AddCircleOutlineIcon, chatBot: AndroidIcon, queue: LowPriorityIcon, redirect: LowPriorityIcon,
  open: PlayArrowIcon, reopen: ReplayIcon, access: VisibilityIcon, transfered: SwapHorizIcon,
  receivedTransfer: CallReceivedIcon, pending: HourglassEmptyIcon, closed: CheckCircleOutlineIcon,
  clientClosed: PersonOutlineIcon, nps: StarBorderIcon,
};

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const line = isLight ? theme.palette.divider : "rgba(255,255,255,0.08)";
  const tone = (hex, key) => ({ backgroundColor: alpha(hex, 0.14), color: stateText(theme, hex, key) });
  return {
    paper: { width: 520, maxWidth: "calc(100vw - 32px)", margin: 16 },
    header: { padding: "18px 20px", gap: 12, alignItems: "center" },
    headIcon: { width: 40, height: 40, borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center", flex: "none", ...tone(AEROS.cyan, "info"), "& .MuiSvgIcon-root": { fontSize: 21 } },
    title: { fontFamily: AEROS.fontDisplay, fontSize: 15, fontWeight: 700, color: theme.palette.text.primary },
    sub: { fontSize: 11.5, color: theme.palette.text.secondary, marginTop: 2 },
    body: { padding: "6px 20px 14px", maxHeight: "60vh", overflowY: "auto" },
    toolbar: { display: "flex", alignItems: "center", gap: 6, padding: "6px 0 10px", fontSize: 12, color: theme.palette.text.secondary },
    summary: { display: "flex", flexWrap: "wrap", gap: 6, paddingBottom: 10, borderBottom: `1px solid ${line}`, marginBottom: 6 },
    sumPill: { fontSize: 10.5, fontWeight: 600, borderRadius: 99, padding: "2px 8px", display: "inline-flex", alignItems: "center", gap: 4 },
    day: { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: theme.palette.aeros.textTertiary, padding: "12px 0 6px" },
    item: { display: "flex", gap: 12, position: "relative", paddingBottom: 14, "&:before": { content: '""', position: "absolute", left: 13, top: 28, bottom: -2, width: 2, backgroundColor: line }, "&:last-child:before": { display: "none" } },
    dot: { width: 28, height: 28, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", flex: "none", zIndex: 1, "& .MuiSvgIcon-root": { fontSize: 15 } },
    info: tone(AEROS.cyan, "info"), success: tone(AEROS.success, "success"), warning: tone(AEROS.warning, "warning"), error: tone(AEROS.error, "error"), purple: tone("#C08BFF", "purple"),
    muted: { backgroundColor: isLight ? theme.palette.action.hover : "rgba(255,255,255,0.06)", color: theme.palette.aeros.textTertiary },
    text: { fontSize: 13, fontWeight: 600, color: theme.palette.text.primary, lineHeight: 1.35 },
    textMuted: { fontWeight: 500, color: theme.palette.text.secondary },
    meta: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginTop: 3, fontSize: 11.5, color: theme.palette.text.secondary },
    chip: { fontSize: 10.5, fontWeight: 600, borderRadius: 6, padding: "1px 7px", backgroundColor: isLight ? theme.palette.action.hover : "rgba(255,255,255,0.06)", color: theme.palette.text.secondary },
    time: { marginLeft: "auto", fontSize: 11.5, color: theme.palette.aeros.textTertiary, whiteSpace: "nowrap", paddingTop: 5, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 },
    elapsed: { fontSize: 10.5, color: stateText(theme, AEROS.cyan, "info") },
    empty: { padding: "28px 0", textAlign: "center", fontSize: 13, color: theme.palette.text.secondary },
    footer: { padding: "12px 20px" },
  };
});

const ShowTicketLogModal = ({ isOpen, handleClose, ticketId, ticketNumber }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const aeros = useAerosSettingsStyles();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [showAccess, setShowAccess] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/tickets-log/${ticketId}`);
        if (alive) setLogs(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [ticketId]);

  const timeline = useMemo(() => buildTimeline(logs, { showAccess }), [logs, showAccess]);
  const counts = useMemo(() => countByType(logs), [logs]);
  const accessCount = counts.access || 0;
  const events = logs.length - accessCount;
  const t = (k, o) => i18n.t(`showTicketLogModal.${k}`, o);

  return (
    <Dialog open={Boolean(isOpen)} onClose={handleClose} maxWidth={false} classes={{ paper: classes.paper }}>
      <div className={m.form}>
        <div className={`${m.header} ${classes.header}`}>
          <div className={classes.headIcon}><HistoryIcon /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={classes.title}>{t("title.header")}{ticketNumber || ticketId ? ` · #${ticketNumber || ticketId}` : ""}</div>
            <div className={classes.sub}>{loading ? t("loading") : t("summary", { events, access: accessCount })}</div>
          </div>
          <IconButton className={m.closeButton} onClick={handleClose} aria-label={i18n.t("showTicketLogModal.close")}><CloseIcon /></IconButton>
        </div>

        <div className={classes.body}>
          {loading ? (
            <div className={classes.empty}><CircularProgress size={22} /></div>
          ) : !logs.length ? (
            <div className={classes.empty}>{t("empty")}</div>
          ) : (
            <>
              <div className={classes.summary}>
                {["transfered", "pending", "closed", "reopen"].filter((k) => counts[k]).map((k) => (
                  <span key={k} className={`${classes.sumPill} ${classes[toneOf(k)]}`}>{counts[k]}× {t(`kinds.${k}`)}</span>
                ))}
                <label className={classes.toolbar} style={{ marginLeft: "auto", padding: 0 }}>
                  <Switch className={aeros.toggleSwitchSmall} checked={showAccess} onChange={(e) => setShowAccess(e.target.checked)} inputProps={{ "aria-label": t("showAccess") }} />
                  {t("showAccess")}{accessCount ? ` (${accessCount})` : ""}
                </label>
              </div>
              {timeline.map((g) => (
                <div key={g.day}>
                  <div className={classes.day}>{g.day}</div>
                  {g.items.map((l) => {
                    const d = describeLog(l);
                    const Icon = ICONS[l.type] || HistoryIcon;
                    const tone = toneOf(l.type);
                    return (
                      <div key={l.id} className={classes.item}>
                        <div className={`${classes.dot} ${classes[tone]}`}><Icon /></div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className={`${classes.text} ${tone === "muted" ? classes.textMuted : ""}`}>
                            {d.text}{l.count > 1 ? ` (${l.count}×)` : ""}
                          </div>
                          {(d.meta || (d.actor && l.type !== "access")) && (
                            <div className={classes.meta}>
                              {d.actor && <span className={classes.chip}>{d.actor}</span>}
                              {d.meta && <span className={classes.chip}>{d.meta}</span>}
                            </div>
                          )}
                        </div>
                        <div className={classes.time}>
                          <span>{timeLabel(l.createdAt)}</span>
                          {l.elapsed && <span className={classes.elapsed}>{l.elapsed}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </>
          )}
        </div>

        <div className={`${m.footer} ${classes.footer}`}>
          <div className={m.footerActions}>
            <Button className={m.cancelButton} onClick={handleClose}>{t("close")}</Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ShowTicketLogModal;
